import { c as _c } from "react/compiler-runtime";
import { postHostedUserConsent } from '@onefootprint/fetch-hosted';
import { IcoIdCard24, IcoShield24, IcoUserstore24 } from '@onefootprint/icons';
import { LEGACY_IDV_CONTAINER_ID as LAYOUT_CONTAINER_ID, StickyBottomBox } from '@onefootprint/idv2';
import { BottomSheet, Button, Dialog } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'usehooks-ts';
import { NavigationHeader } from '@/idv/components';
import { type DeviceInfo, useIdvRequestErrorToast } from '@/idv/hooks';
import { isMobileKind } from '../../../utils/capture';
import ConsentText from './components/consent-text';
import MergedLogos from './components/merged-logos';
const mediaBreakpoints = {
  md: '900px'
};
type ConsentProps = {
  authToken: string;
  device: DeviceInfo;
  onConsentSubmit: () => void;
  onNavigatedToPrev: () => void;
  tenantLogoUrl?: string;
  tenantName: string;
  tenantPrivacyPolicyUrl?: string;
};
const Consent = t0 => {
  const $ = _c(101);
  const {
    authToken,
    device,
    onConsentSubmit,
    onNavigatedToPrev,
    tenantLogoUrl,
    tenantName,
    tenantPrivacyPolicyUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.id-doc.pages.consent"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const [consentTextOpen, setConsentTextOpen] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${mediaBreakpoints.md})`);
  const requestErrorToast = useIdvRequestErrorToast();
  let t2;
  if ($[1] !== authToken || $[2] !== t) {
    t2 = async () => {
      const descriptionWithoutHyperlinks = String(t(_temp)).replace(/<\/?privacy>|<\/?toc>|<\/?incode_privacy>|<\/?incodePrivacy>|<br\/>/g, "");
      const consentLanguages = [t(_temp2), descriptionWithoutHyperlinks];
      consentLanguages.push(t(_temp3));
      consentLanguages.push(t(_temp4));
      const consentLanguageText = consentLanguages.join(". ");
      await postHostedUserConsent({
        body: {
          consentLanguageText,
          mlConsent: false
        },
        headers: {
          "X-Fp-Authorization": authToken
        }
      });
    };
    $[1] = authToken;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const submitConsent = t2;
  let t3;
  if ($[4] !== requestErrorToast) {
    t3 = error => {
      requestErrorToast(error);
    };
    $[4] = requestErrorToast;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onConsentSubmit) {
    t4 = () => {
      onConsentSubmit();
    };
    $[6] = onConsentSubmit;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== submitConsent || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      mutationFn: submitConsent,
      onError: t3,
      onSuccess: t4
    };
    $[8] = submitConsent;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const consentMutation = useMutation(t5);
  let t6;
  if ($[12] !== consentMutation) {
    t6 = () => {
      consentMutation.mutate();
    };
    $[12] = consentMutation;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleConsent = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 1,
      transition: {
        duration: 0.3
      }
    };
    t9 = {
      opacity: 0
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== onNavigatedToPrev) {
    t10 = <NavigationHeader leftButton={{
      variant: "back",
      onBack: onNavigatedToPrev
    }} />;
    $[17] = onNavigatedToPrev;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== tenantLogoUrl || $[20] !== tenantName) {
    t11 = <MergedLogos tenantLogoUrl={tenantLogoUrl} tenantName={tenantName} />;
    $[19] = tenantLogoUrl;
    $[20] = tenantName;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t || $[23] !== tenantName) {
    t12 = t(_temp5, {
      tenantName
    });
    $[22] = t;
    $[23] = tenantName;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <p className="text-heading-3 text-primary text-center">{t12}</p>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t) {
    t14 = t(_temp6);
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <p className="text-body-2 text-secondary text-center">{t14}</p>;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t13 || $[32] !== t15) {
    t16 = <div className="flex flex-col gap-2">{t13}{t15}</div>;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="shrink-0"><IcoIdCard24 color="primary" /></div>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== device) {
    t18 = $_5 => isMobileKind(device.type) ? $_5["take-a-photo"] : $_5["upload-a-photo"];
    $[35] = device;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t || $[38] !== t18) {
    t19 = t(t18);
    $[37] = t;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t19) {
    t20 = <div className="flex flex-row gap-2 items-start">{t17}<p className="text-label-2 text-primary mt-0.5">{t19}</p></div>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="shrink-0"><IcoUserstore24 color="primary" /></div>;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t(_temp7);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <div className="flex flex-row gap-2 items-start">{t21}<p className="text-label-2 text-primary mt-0.5">{t22}</p></div>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="shrink-0"><IcoShield24 color="primary" /></div>;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t || $[49] !== tenantName) {
    t25 = t(_temp8, {
      tenantName
    });
    $[48] = t;
    $[49] = tenantName;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t25) {
    t26 = <div className="flex flex-row gap-2 items-start">{t24}<p className="text-label-2 text-primary mt-0.5">{t25}</p></div>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t20 || $[54] !== t23 || $[55] !== t26) {
    t27 = <div className="flex flex-col w-full gap-5">{t20}{t23}{t26}</div>;
    $[53] = t20;
    $[54] = t23;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t11 || $[58] !== t16 || $[59] !== t27) {
    t28 = <div className="flex flex-col items-center gap-6">{t11}{t16}{t27}</div>;
    $[57] = t11;
    $[58] = t16;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== consentTextOpen || $[62] !== isMobile) {
    t29 = isMobile ? <BottomSheet open={consentTextOpen} onClose={() => setConsentTextOpen(false)} containerId={LAYOUT_CONTAINER_ID}><ConsentText /></BottomSheet> : <Dialog open={consentTextOpen} onClose={() => setConsentTextOpen(false)} title=""><ConsentText /></Dialog>;
    $[61] = consentTextOpen;
    $[62] = isMobile;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const t30 = tenantPrivacyPolicyUrl ? "document-flow.id-doc.pages.consent.privacy-policy-text-with-tenant" : "document-flow.id-doc.pages.consent.privacy-policy-text-without-tenant";
  let t31;
  if ($[64] !== tenantName) {
    t31 = {
      tenantName
    };
    $[64] = tenantName;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span role="button" tabIndex={0} className="text-secondary underline cursor-pointer" onClick={() => setConsentTextOpen(true)} onKeyDown={e => {
      if (e.key === "Enter") {
        e.preventDefault();
        setConsentTextOpen(true);
      }
    }} />;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t) {
    t33 = t(_temp9);
    $[67] = t;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== t33) {
    t34 = <a className="text-secondary underline cursor-pointer" href="https://www.onefootprint.com/privacy-policy" rel="noopener noreferrer" target="_blank" aria-label="Read Footprint Privacy Policy"><span className="sr-only">{t33}</span></a>;
    $[69] = t33;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  const t35 = `Read ${tenantName}'s Privacy Policy`;
  let t36;
  if ($[71] !== t || $[72] !== tenantName) {
    t36 = t(_temp10, {
      tenantName
    });
    $[71] = t;
    $[72] = tenantName;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== t36) {
    t37 = <span className="sr-only">{t36}</span>;
    $[74] = t36;
    $[75] = t37;
  } else {
    t37 = $[75];
  }
  let t38;
  if ($[76] !== t35 || $[77] !== t37 || $[78] !== tenantPrivacyPolicyUrl) {
    t38 = <a className="text-secondary underline cursor-pointer" href={tenantPrivacyPolicyUrl} rel="noopener noreferrer" target="_blank" aria-label={t35}>{t37}</a>;
    $[76] = t35;
    $[77] = t37;
    $[78] = tenantPrivacyPolicyUrl;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t34 || $[81] !== t38) {
    t39 = {
      bottomsheet: t32,
      fpPrivacyPolicy: t34,
      tenantPrivacyPolicy: t38
    };
    $[80] = t34;
    $[81] = t38;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] !== t30 || $[84] !== t31 || $[85] !== t39) {
    t40 = <p className="text-caption-1 text-quaternary text-center"><Trans ns="idv" i18nKey={t30} values={t31} components={t39} /></p>;
    $[83] = t30;
    $[84] = t31;
    $[85] = t39;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  const t41 = consentMutation.isPending;
  let t42;
  if ($[87] !== t) {
    t42 = t(_temp11);
    $[87] = t;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== consentMutation.isPending || $[90] !== handleConsent || $[91] !== t42) {
    t43 = <Button fullWidth={true} size="large" onClick={handleConsent} loading={t41}>{t42}</Button>;
    $[89] = consentMutation.isPending;
    $[90] = handleConsent;
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] !== t40 || $[94] !== t43) {
    t44 = <StickyBottomBox><div className="flex flex-col gap-3 items-center">{t40}{t43}</div></StickyBottomBox>;
    $[93] = t40;
    $[94] = t43;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== t10 || $[97] !== t28 || $[98] !== t29 || $[99] !== t44) {
    t45 = <motion.div className="flex flex-col flex-1 h-full" initial={t7} animate={t8} exit={t9}>{t10}{t28}{t29}{t44}</motion.div>;
    $[96] = t10;
    $[97] = t28;
    $[98] = t29;
    $[99] = t44;
    $[100] = t45;
  } else {
    t45 = $[100];
  }
  return t45;
};
export default Consent;
function _temp($) {
  return $["consent-text"].description;
}
function _temp2($_0) {
  return $_0["consent-text"].subtitle;
}
function _temp3($_1) {
  return $_1["consent-text"]["third-party-consent"];
}
function _temp4($_2) {
  return $_2["consent-text"].cta;
}
function _temp5($_3) {
  return $_3.title;
}
function _temp6($_4) {
  return $_4.subtitle;
}
function _temp7($_6) {
  return $_6["footprint-securely-verifies"];
}
function _temp8($_7) {
  return $_7["your-data-encrypted"];
}
function _temp9($_8) {
  return $_8["footprint-privacy-policy"];
}
function _temp10($_9) {
  return $_9["tenant-privacy-policy"];
}
function _temp11($_10) {
  return $_10["agree-and-continue"];
}